import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sliders, X, Search, Check, Loader2, MapPin, Filter, Sparkles, Zap, Brain, Users, Activity, Clock, Target, User, Heart, Award, TrendingDown, Star, ChevronDown, ChevronUp, LayoutGrid, List, Building2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useSearchSubmission } from '../../hooks/matchmaking/useSearchSubmission';
import TherapistCard from './TherapistCard';
import useFetchSpeciality from '../../hooks/Resources/speciality/useFetchSpeciality';
import useFetchModalityResource from '../../hooks/Resources/modality/useFetchModalityResource';
import { useFetchAgeRange } from '../../hooks/Resources/age/useFetchAgeRange';
import { useFetchInsuranceResource } from '../../hooks/Resources/insurance/useFetchInsuranceResource';
import useFetchSessionTypeResource from '../../hooks/Resources/session/useFetchSessionTypeResource'
import useFetchLanguage from '../../hooks/Resources/language/useLanguagesResource';

// Centralized filter configuration 
const FILTER_CONFIG = {
  urgencyLevels: ['None', 'Standard', 'High', 'Highest'],
  states: ['CA', 'FL', 'IL', 'MA', 'NJ', 'NY', 'PA', 'TX', 'WA'],
  specialties: [] as string[],
  treatmentModalities: [] as string[],
  ages: [] as string[],
  clientTypes: [
    'Children (1-12)', 'Adolescents / Teens (13–17)', 'Adults (18–64)',
    'Seniors / Elderly (65+)'
  ],
  sessionTypes: ['couples', 'family', 'group', 'individual'],
  languages: ['English', 'French', 'German', 'Mandarin', 'Spanish'],
  verificationStatus: ['pending', 'unverified', 'verified'],
  billingStatus: ['active', 'inactive', 'suspended'],
  insuranceProviders: [
    'Aetna', 'Blue Cross Blue Shield', 'Cigna', 'Humana', 'Kaiser Permanente',
    'Medicaid', 'Medicare', 'Tricare', 'UnitedHealthcare'
  ],
  professionalTitles: [
    'LCPC', 'LCSW', 'LMFT', 'LMSW', 'LMHC', 'LPC', 'LMHC', 'PhD', 'PsyD', 'Psychiatrist', 'Psychologist'
  ],
  professionalLicenseTypes: [
    'CBT certified', 'DBT certified', 'EMDR certified', 'NPI on file', 'State licensed', 'Trauma-informed'
  ],
};

// US state full-name → abbreviation (used for Nominatim autocomplete formatting)
const STATE_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
  Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
  'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA',
  'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN',
  Texas: 'TX', Utah: 'UT', Vermont: 'VT', Virginia: 'VA', Washington: 'WA',
  'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
  'District of Columbia': 'DC',
};

// Default and initial filter states - matching backend parameters exactly
const DEFAULT_FILTERS = {
  // Status filters
  verificationStatus: '',
  billingStatus: '',
  isActive: true,

  // Location filters
  state: '',
  city: '',
  zipCode: '',
  timezone: '',
  /** Multi-select practice locations for matchmaking */
  location: [] as string[],

  // Availability filters
  acceptingClients: false,
  minOpenSpots: 0,
  urgencyLevel: 'None',
  availableWeekdays: [],
  availableWeekends: false,
  underCapacity: false,

  // Client and session filters
  minTargetWeeklyClients: 0,
  maxCurrentClients: 0,
  clientTypes: [],
  sessionTypes: [],

  // Professional qualifications
  specialties: [],
  treatmentModalities: [],
  languages: [],
  ageRange: [],
  hasMultipleLicenses: false,
  minYearLicensed: 0,
  maxYearLicensed: new Date().getFullYear(),
  yearsOfExperience: 0,
  numberOfWeeklySessions: 0,

  // Insurance and rates
  acceptsInsurance: false,
  insuranceProviders: [],
  maxIndividualRate: 0,
  maxFamilyRate: 0,

  /** Practice office (dropdown); distinct from city/state */
  officeLocation: '',

  /** Professional qualifications — multi-select */
  titles: [] as string[],
  professionalLicenses: [] as string[],
};

const INITIAL_FILTERS = { ...DEFAULT_FILTERS };

// Component: Filter Section Header
const FilterSectionHeader = ({ icon, title, activeCount, gradient, iconBg, textColor }) => (
  <div className="flex items-center gap-3 mb-4 sm:mb-6">
    <div className={`w-8 h-8 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
      {typeof icon === 'string' ? (
        <span className="text-white text-sm">{icon}</span>
      ) : (
        React.cloneElement(icon, { className: "h-4 w-4 text-white" })
      )}
    </div>
    <h3 className={`text-lg font-bold ${textColor} flex-1`}>{title}</h3>
    {activeCount > 0 && (
      <span className={`${iconBg} text-white text-xs font-bold px-3 py-1 rounded-full flex-shrink-0`}>
        {activeCount}
      </span>
    )}
  </div>
);

// Component: Location Filter Section
const LocationSection = ({ filters, onFilterChange }) => (
  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-100">
    <FilterSectionHeader
      icon={<MapPin />}
      title="Location"
      iconBg="bg-emerald-500"
      textColor="text-emerald-900"
    />
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
      <div className="flex-1">
        <label className="block text-sm font-semibold text-emerald-800 mb-2">City</label>
        <input
          type="text"
          value={filters.city}
          onChange={(e) => onFilterChange('city', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-emerald-500 transition-all duration-300 text-sm sm:text-base"
          placeholder="Enter city"
        />
      </div>
      <div className="flex-1 sm:max-w-[200px]">
        <label className="block text-sm font-semibold text-emerald-800 mb-2">State</label>
        <select
          value={filters.state}
          onChange={(e) => onFilterChange('state', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-emerald-500 transition-all duration-300 text-sm sm:text-base"
        >
          <option value="">Any State</option>
          {FILTER_CONFIG.states.map((state) => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>
      <div className="flex-1 sm:max-w-[200px]">
        <label className="block text-sm font-semibold text-emerald-800 mb-2">ZIP Code</label>
        <input
          type="text"
          value={filters.zipCode}
          onChange={(e) => onFilterChange('zipCode', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-emerald-500 transition-all duration-300 text-sm sm:text-base"
          placeholder="ZIP"
        />
      </div>
      <div className="flex-1 sm:max-w-[200px]">
        <label className="block text-sm font-semibold text-emerald-800 mb-2">Timezone</label>
        <select
          value={filters.timezone}
          onChange={(e) => onFilterChange('timezone', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-emerald-500 transition-all duration-300 text-sm sm:text-base"
        >
          <option value="">Any</option>
          <option value="EST">EST</option>
          <option value="CST">CST</option>
          <option value="MST">MST</option>
          <option value="PST">PST</option>
          <option value="UTC">UTC</option>
        </select>
      </div>
    </div>
  </div>
);

// Component: Availability Section
const AvailabilitySection = ({ filters, onFilterChange }) => (
  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-100">
    <FilterSectionHeader
      icon={<Zap />}
      title="Availability"
      iconBg="bg-blue-500"
      textColor="text-blue-900"
    />
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
      <div className="flex-1">
        <label className="block text-sm font-semibold text-blue-800 mb-2">Status</label>
        <select
          value={filters.verificationStatus}
          onChange={(e) => onFilterChange('verificationStatus', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm sm:text-base"
        >
          <option value="">Any Status</option>
          {FILTER_CONFIG.verificationStatus.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1 sm:max-w-[200px]">
        <label className="block text-sm font-semibold text-blue-800 mb-2">Min Open Spots</label>
        <input
          type="number"
          min="0"
          value={filters.minOpenSpots}
          onChange={(e) => onFilterChange('minOpenSpots', parseInt(e.target.value) || 0)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm sm:text-base"
          placeholder="0"
        />
      </div>
      <div className="flex-1 sm:max-w-[200px]">
        <label className="block text-sm font-semibold text-blue-800 mb-2">Urgency</label>
        <select
          value={filters.urgencyLevel}
          onChange={(e) => onFilterChange('urgencyLevel', e.target.value)}
          className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm sm:text-base"
        >
          <option value="">Any Urgency</option>
          {FILTER_CONFIG.urgencyLevels.map((level) => (
            <option key={level} value={level}>{level}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

// Component: Pricing Section
const PricingSection = ({ filters, onFilterChange }) => (
  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-100">
    <FilterSectionHeader
      icon="$"
      title="Session Rates"
      iconBg="bg-amber-500"
      textColor="text-amber-900"
    />
    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
      <div className="flex-1">
        <label className="block text-sm font-semibold text-amber-800 mb-2">Max Individual Rate</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
            <span className="text-amber-600 font-semibold">$</span>
          </div>
          <input
            type="number"
            min="0"
            step="10"
            value={filters.maxIndividualRate}
            onChange={(e) => onFilterChange('maxIndividualRate', parseInt(e.target.value) || 0)}
            className="pl-7 sm:pl-8 w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-amber-500 transition-all duration-300 text-sm sm:text-base"
            placeholder="150"
          />
        </div>
      </div>

      <div className="flex-1">
        <label className="block text-sm font-semibold text-amber-800 mb-2">Max Family Rate</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
            <span className="text-amber-600 font-semibold">$</span>
          </div>
          <input
            type="number"
            min="0"
            step="10"
            value={filters.maxFamilyRate}
            onChange={(e) => onFilterChange('maxFamilyRate', parseInt(e.target.value) || 0)}
            className="pl-7 sm:pl-8 w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-amber-500 transition-all duration-300 text-sm sm:text-base"
            placeholder="200"
          />
        </div>
      </div>
    </div>
  </div>
);

// ─── Popular Filters: Location (autocomplete) / Weekly Sessions / Urgency ────
interface MetricsSectionProps {
  filters: any;
  onFilterChange: (key: string, value: any) => void;
  locationInput: string;
  onLocationInputChange: (v: string) => void;
  onAddLocation: () => void;
  onRemoveLocation: (v: string) => void;
  /** Canonical office locations derived from the practice record */
  practiceLocations: string[];
}

function MetricsSection({
  filters,
  onFilterChange,
  locationInput,
  onLocationInputChange,
  onRemoveLocation,
  practiceLocations,
}: MetricsSectionProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Live ref so addSuggestion always reads the latest location array ──────
  // This prevents the stale-closure bug where a second suggestion click
  // would capture the old (pre-first-add) array and overwrite it.
  const locationRef = useRef<string[]>(filters.location || []);
  useEffect(() => {
    locationRef.current = filters.location || [];
  }, [filters.location]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced Nominatim fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = locationInput.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoadingSuggestions(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&countrycodes=us&limit=8`,
          { headers: { 'Accept-Language': 'en-US,en' } }
        );
        const data: any[] = await res.json();
        const seen = new Set<string>();
        const formatted: string[] = [];
        for (const item of data) {
          const addr = item.address ?? {};
          const city = addr.city || addr.town || addr.village || addr.suburb || addr.county || '';
          const stateAbbr = STATE_ABBR[addr.state] ?? addr.state ?? '';
          const label = city && stateAbbr
            ? `${city}, ${stateAbbr}`
            : (stateAbbr || city || item.display_name?.split(',')[0] || '');
          if (label && !seen.has(label)) {
            seen.add(label);
            formatted.push(label);
          }
        }
        setSuggestions(formatted);
        setShowSuggestions(formatted.length > 0);
        setHighlightedIdx(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 380);
  }, [locationInput]);

  // Uses locationRef (not filters.location) to always get the freshest array
  const addSuggestion = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setShowSuggestions(false);
    onLocationInputChange('');
    const current = locationRef.current;
    if (!current.includes(trimmed)) {
      onFilterChange('location', [...current, trimmed]);
    }
  }, [onFilterChange, onLocationInputChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        const v = locationInput.trim();
        if (v) addSuggestion(v);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && suggestions[highlightedIdx]) {
        addSuggestion(suggestions[highlightedIdx]);
      } else {
        const v = locationInput.trim();
        if (v) addSuggestion(v);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [showSuggestions, highlightedIdx, suggestions, locationInput, addSuggestion]);

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-indigo-100">
      <FilterSectionHeader
        icon={<Activity />}
        title="Popular Filters"
        iconBg="bg-indigo-500"
        textColor="text-indigo-900"
        activeCount={0}
      />

      <div className="space-y-6">

        {/* ── Office Locations (from practice) ─────────────────── */}
        <div>
          <label className="block text-sm font-semibold text-indigo-800 mb-3 pl-2 border-l-4 border-indigo-400 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-500" />
            Office Locations
            <span className="font-normal text-indigo-500 text-xs">— select which offices to include</span>
          </label>

          {practiceLocations.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-4">
              {practiceLocations.map((loc) => {
                const active = filters.location?.includes(loc);
                return (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => {
                      const current: string[] = filters.location || [];
                      onFilterChange(
                        'location',
                        active ? current.filter((l) => l !== loc) : [...current, loc]
                      );
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                      active
                        ? 'bg-indigo-500 text-white border-indigo-500 shadow-sm scale-105'
                        : 'bg-white/80 text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300'
                    }`}
                  >
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    {loc}
                    {active && <Check className="w-3 h-3 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-indigo-400 mb-4 italic">No office locations found on this practice record.</p>
          )}

          {/* Additional location search (for locations outside the practice list) */}
          <label className="block text-xs font-semibold text-indigo-600 mb-2 mt-1">
            Add another location
          </label>
          <div ref={wrapperRef} className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                {isLoadingSuggestions ? (
                  <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 animate-spin pointer-events-none" />
                ) : (
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 pointer-events-none" />
                )}
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => onLocationInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  autoComplete="off"
                  className="w-full pl-9 pr-3 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-indigo-500 transition-all duration-300 text-sm"
                  placeholder="e.g. Miami, New York, Philadelphia…"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions}
                />
              </div>
              <button
                type="button"
                onClick={() => addSuggestion(locationInput)}
                disabled={!locationInput.trim()}
                className="px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl bg-indigo-500 text-white text-sm font-semibold shadow-sm hover:bg-indigo-600 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0"
              >
                Add
              </button>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-40 left-0 right-0 mt-1.5 bg-white/95 backdrop-blur-md border border-indigo-100 rounded-xl shadow-xl overflow-hidden"
                role="listbox"
              >
                {suggestions.map((s, idx) => (
                  <li
                    key={s}
                    role="option"
                    aria-selected={idx === highlightedIdx}
                    onMouseDown={(e) => { e.preventDefault(); addSuggestion(s); }}
                    onMouseEnter={() => setHighlightedIdx(idx)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                      idx === highlightedIdx
                        ? 'bg-indigo-50 text-indigo-800'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${idx !== suggestions.length - 1 ? 'border-b border-gray-100' : ''}`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Extra (non-practice) location chips */}
          {filters.location?.filter((loc: string) => !practiceLocations.includes(loc)).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {filters.location
                .filter((loc: string) => !practiceLocations.includes(loc))
                .map((loc: string) => (
                  <span
                    key={loc}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-white/80 backdrop-blur-sm border border-indigo-200 text-indigo-700 text-xs font-medium rounded-full shadow-sm"
                  >
                    <MapPin className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                    {loc}
                    <button
                      type="button"
                      onClick={() => onRemoveLocation(loc)}
                      className="ml-0.5 hover:text-indigo-900 transition-colors"
                      aria-label={`Remove ${loc}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
            </div>
          )}
        </div>

        {/* ── Weekly Sessions + Urgency side-by-side ────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">

          {/* Weekly Sessions */}
          <div>
            <label className="block text-sm font-semibold text-indigo-800 mb-3 pl-2 border-l-4 border-violet-400">
              Weekly Sessions <span className="font-normal text-indigo-500 text-xs">(min / week)</span>
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="40"
                value={filters.numberOfWeeklySessions || ''}
                onChange={(e) => onFilterChange('numberOfWeeklySessions', parseInt(e.target.value) || 0)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border-0 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-violet-500 transition-all duration-300 text-sm"
                placeholder="Any (e.g. 5)"
              />
              {filters.numberOfWeeklySessions > 0 && (
                <button
                  type="button"
                  onClick={() => onFilterChange('numberOfWeeklySessions', 0)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Urgency Level */}
          <div>
            <label className="block text-sm font-semibold text-indigo-800 mb-3 pl-2 border-l-4 border-amber-400">
              Urgency Level
            </label>
            <div className="flex flex-wrap gap-2">
              {(['None', 'Standard', 'High', 'Highest'] as const).map((level) => {
                const active = (filters.urgencyLevel || 'None') === level;
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => onFilterChange('urgencyLevel', level)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                      active
                        ? 'bg-white shadow-lg border-2 border-indigo-300 scale-105 font-semibold text-indigo-700'
                        : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105 text-gray-600'
                    }`}
                  >
                    {level === 'None' ? 'Any' : level}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Under Capacity Only */}
        <div className="pt-4 border-t border-indigo-100/60">
          <button
            type="button"
            onClick={() => onFilterChange('underCapacity', !filters.underCapacity)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
              filters.underCapacity
                ? 'bg-white shadow-lg border-2 border-indigo-300 scale-105 font-semibold'
                : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105'
            }`}
          >
            <TrendingDown className="w-3.5 h-3.5" />
            Under Capacity Only
          </button>
        </div>
      </div>
    </div>
  );
}

// Component: Multi-select Filter Section
const MultiSelectSection = ({ title, filterKey, options, filters, onToggle, config }) => (
  <div className={`bg-gradient-to-br ${config.gradient} rounded-xl sm:rounded-2xl p-4 sm:p-6 border ${config.border}`}>
    <FilterSectionHeader
      icon={config.icon}
      title={title}
      activeCount={filters[filterKey]?.length || 0}
      iconBg={config.bg}
      textColor={config.text}
    />
    <div className="flex flex-wrap gap-2 sm:gap-3 max-h-60 overflow-y-auto">
      {options.map((option) => (
        <button
          type="button"
          key={option}
          onClick={() => onToggle(filterKey, option)}
          className={`px-4 py-2.5 sm:px-5 sm:py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${filters[filterKey]?.includes(option)
            ? `bg-white shadow-lg border-2 ${config.border.replace('border-', 'border-2 border-')} scale-105 font-semibold`
            : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105'
            }`}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

// Component: Results View - FIXED VERSION
interface SearchResults {
  data: Array<{
    _id: string;
    [key: string]: any;
  }>;
}

interface SelectedFilter {
  key: string;
  label: string;
  category: string;
}

type ResultsViewMode = 'gallery' | 'list';

interface ResultsViewProps {
  searchResults: SearchResults;
  selectedFilters: SelectedFilter[];
  viewMode: ResultsViewMode;
  onViewModeChange: (mode: ResultsViewMode) => void;
  onBackToFilters: () => void;
  onRemoveFilter: (filter: SelectedFilter) => void;
  onClearAllFilters: () => void;
}

const ResultsView = ({
  searchResults,
  selectedFilters,
  viewMode,
  onViewModeChange,
  onBackToFilters,
  onRemoveFilter,
  onClearAllFilters
}: ResultsViewProps) => {
  const therapists = Array.isArray(searchResults?.data) ? searchResults.data : [];

  return (
  <div className="space-y-6 sm:space-y-10 px-4 sm:px-6">
    <div className="bg-white/90 backdrop-blur-xl shadow-sm border border-white/20 p-4 sm:p-6 rounded-xl sm:rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {therapists.length || 0} Perfect Matches
            </h2>
            <p className="text-sm sm:text-base text-gray-600">Curated for your specific needs</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="inline-flex items-center p-1 rounded-xl bg-gray-100">
            <button
              type="button"
              onClick={() => onViewModeChange('gallery')}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === 'gallery'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              Gallery
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('list')}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
              List
            </button>
          </div>
          {selectedFilters.length > 0 && (
            <button
              type="button"
              onClick={onClearAllFilters}
              className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 text-gray-700 rounded-xl sm:rounded-2xl hover:bg-gray-200 transition-all duration-300 font-medium text-sm sm:text-base"
            >
              <X className="h-4 w-4" />
              <span>Clear All</span>
            </button>
          )}
          <button
            type="button"
            onClick={onBackToFilters}
            className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-xl sm:rounded-2xl hover:from-purple-200 hover:to-blue-200 transition-all duration-300 font-medium text-sm sm:text-base flex-shrink-0"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">Refine Search</span>
            <span className="sm:hidden">Refine</span>
          </button>
        </div>
      </div>

      {selectedFilters.length > 0 && (
        <div className="mt-4 sm:mt-6 flex flex-wrap gap-2">
          {selectedFilters.map((filter) => (
            <span
              key={filter.key}
              className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${filter.category === 'location' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                filter.category === 'specialties' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                  filter.category === 'treatment' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                    filter.category === 'price' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                      'bg-gray-100 text-gray-700 border border-gray-200'
                }`}
            >
              <span className="truncate max-w-[100px] sm:max-w-none inline-block">{filter.label}</span>
              <button
                type="button"
                onClick={() => onRemoveFilter(filter)}
                className="ml-2 hover:bg-black/10 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>

    <div
      className={
        viewMode === 'gallery'
          ? 'grid grid-cols-1 lg:grid-cols-2 gap-4'
          : 'space-y-4 relative max-w-4xl mx-auto'
      }
    >
      {therapists.length > 0 ? (
        therapists.map((therapist, index) => (
          <div key={therapist._id || index} className={viewMode === 'gallery' ? 'h-full' : ''}>
            <TherapistCard
              therapist={therapist}
              index={index}
              variant={viewMode}
            />
          </div>
        ))
      ) : (
        <div className="text-center py-12 text-gray-500 bg-white/70 rounded-2xl border border-gray-200">
          <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No clinicians found matching your criteria</p>
          <p className="text-sm mt-1">Try removing one or two filters and search again</p>
        </div>
      )}
    </div>
  </div>
  );
};

// Component: Loading Modal
const LoadingModal = ({ isVisible, message }) => {
  if (!isVisible) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 flex items-center gap-4 max-w-sm w-full shadow-2xl border border-white/50">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Loader2 className="animate-spin h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800">Searching…</p>
          <p className="text-xs text-gray-500 mt-0.5">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Component: Error Modal
const ErrorModal = ({ error, onClose }) => {
  if (!error) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 max-w-md w-full shadow-2xl border border-red-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <X className="h-4 w-4 text-red-600" />
          </div>
          <h3 className="text-base font-bold text-red-700 border-l-4 border-red-400 pl-2">Search Error</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">{error}</p>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors shadow-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

// Component: Success Modal
const SuccessModal = ({ isVisible, resultCount, onViewResults }) => {
  if (!isVisible) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 max-w-md w-full shadow-2xl border border-emerald-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Check className="h-4 w-4 text-emerald-600" />
          </div>
          <h3 className="text-base font-bold text-emerald-700 border-l-4 border-emerald-400 pl-2">Match Complete</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          Found <span className="font-bold text-gray-800">{resultCount || 0}</span> clinicians matching your criteria.
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onViewResults}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-sm"
          >
            View Results
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component - OPTIMIZED VERSION
const MatchmakingFilters = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showResults, setShowResults] = useState(false);
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const [resultsViewMode, setResultsViewMode] = useState<ResultsViewMode>('gallery');
  const [locationInput, setLocationInput] = useState('');

  // Derive the practice's office locations from Redux (state.practices is the correct key)
  const practice = useSelector((state: any) => state.practices?.selectedPractice ?? state.practices?.practice ?? null);
  const practiceLocations = useMemo<string[]>(() => {
    const locs: any[] = practice?.locations ?? [];
    return locs
      .map((l) => {
        const city = l.city?.trim();
        const state = l.state?.trim();
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return null;
      })
      .filter(Boolean) as string[];
  }, [practice]);

  // Pre-select all practice locations when they first load
  const practiceLocationsKey = practiceLocations.join('|');
  useEffect(() => {
    if (practiceLocations.length > 0) {
      setFilters((prev) => ({
        ...prev,
        location: practiceLocations,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceLocationsKey]);

  const { specialties, loading: specialtiesLoading, error: specialtiesError } = useFetchSpeciality();
  const { modalities, loading: modalitiesLoading } = useFetchModalityResource();
  const { ages, loading: agesLoading, error: agesError, fetchAgeRanges } = useFetchAgeRange();
  const { insurances, isLoading: insurancesLoading, error: insurancesError, fetchInsuranceData } = useFetchInsuranceResource();
  const { sessionTypes, isLoading: sessionTypeLoading, fetchSessionTypeData } = useFetchSessionTypeResource();
  const { languages, isLoading: languageLoading, fetchLanguages } = useFetchLanguage();

  const {
    isSubmitting,
    submitError,
    showSuccessModal,
    searchResults,
    submitMatchMaking,
    clearError,
    setSuccessModal,
    resetState
  } = useSearchSubmission();

  // Update specialties when they're fetched
  useEffect(() => {
    if (specialties.length > 0) {
      FILTER_CONFIG.specialties = specialties.map(s => s.name).sort((a, b) => a.localeCompare(b));
    }
  }, [specialties]);

  useEffect(() => {
    if (sessionTypes.length > 0) {
      FILTER_CONFIG.sessionTypes = sessionTypes.map(s => s.name).sort((a, b) => a.localeCompare(b));
    }
  }, [sessionTypes])

  useEffect(() => {
    if (insurances.length > 0) {
      FILTER_CONFIG.insuranceProviders = insurances.map(i => i.company_name).sort((a, b) => a.localeCompare(b));
    }
  }, [insurances]);

  useEffect(() => {
    if (modalities.length > 0) {
      FILTER_CONFIG.treatmentModalities = modalities.map(s => s.name).sort((a, b) => a.localeCompare(b));
    }
  }, [modalities]);

  useEffect(() => {
    if (ages.length === 0) {
      fetchAgeRanges();
    }
    if (insurances.length === 0) {
      fetchInsuranceData();
    }

    if (sessionTypes.length === 0) {
      fetchSessionTypeData();
    }

    if (languages.length === 0) {
      fetchLanguages();
    }

  }, [ages, insurances]);


  useEffect(() => {
    if (ages.length > 0) {
      FILTER_CONFIG.ages = ages.map(a => a.range_name).sort((a, b) => a.localeCompare(b));
    }
  }, [ages]);

  // Event handlers
  const onFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const onClearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS, location: practiceLocations });
    setShowResults(false);
  }, [practiceLocations]);

  const toggleArrayFilter = useCallback((filterKey, value) => {
    const currentArray = filters[filterKey] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    onFilterChange(filterKey, newArray);
  }, [filters, onFilterChange]);

  const addLocationFilter = useCallback(() => {
    const value = locationInput.trim();
    if (!value) return;
    const current = filters.location || [];
    if (current.includes(value)) {
      setLocationInput('');
      return;
    }
    onFilterChange('location', [...current, value]);
    setLocationInput('');
  }, [filters.location, locationInput, onFilterChange]);

  const removeLocationFilter = useCallback((value: string) => {
    const current = filters.location || [];
    onFilterChange('location', current.filter((item: string) => item !== value));
  }, [filters.location, onFilterChange]);

  const submitSearch = useCallback(async (searchData) => {
    try {
      const formattedData = { ...searchData };
      await submitMatchMaking(formattedData, () => {
      });
      // Don't set showResults here - let the Redux state handle it
    } catch (error) {
      console.error('Search submission failed:', error);
    }
  }, [submitMatchMaking]);

  const handleSubmit = useCallback(async () => {
    // Clean up filters to remove empty arrays and values
    const cleanedFilters = { ...filters };

    // Remove empty arrays
    Object.keys(cleanedFilters).forEach(key => {
      const value = cleanedFilters[key];
      if (Array.isArray(value) && value.length === 0) {
        delete cleanedFilters[key];
      }
      // Remove empty strings, 0 values, and false values for non-boolean fields
      else if (value === '' || value === 0 || value === null || value === undefined) {
        if (key !== 'isActive' && key !== 'acceptingClients' && key !== 'availableWeekends' && key !== 'acceptsInsurance') {
          delete cleanedFilters[key];
        }
      }
    });

    const formattedData = {
      filters: cleanedFilters,
      timestamp: new Date().toISOString()
    };

    await submitSearch(formattedData);
  }, [filters, submitSearch]);

  const handleViewResults = useCallback(() => {
    setSuccessModal(false);
    setShowResults(true);
  }, [setSuccessModal]);

  // FIXED: Computed selectedFilters based on current filter state
  const selectedFilters = useMemo(() => {
    const result = [];

    if (filters.verificationStatus) {
      result.push({
        key: 'verificationStatus',
        label: `Status: ${filters.verificationStatus}`,
        category: 'status'
      });
    }

    if (filters.state || filters.city || filters.zipCode) {
      const locationParts = [filters.city, filters.state, filters.zipCode].filter(Boolean);
      result.push({
        key: 'location',
        label: locationParts.join(', '),
        category: 'location'
      });
    }

    if (filters.officeLocation) {
      const officeLabels: Record<string, string> = {
        '1': 'Main office',
        '2': 'Downtown branch',
        '3': 'West side office',
      };
      result.push({
        key: 'officeLocation',
        label: `Office: ${officeLabels[filters.officeLocation] ?? filters.officeLocation}`,
        category: 'location'
      });
    }

    if (filters.location?.length > 0) {
      result.push({
        key: 'practiceLocations',
        label: filters.location.length === 1
          ? `Location: ${filters.location[0]}`
          : `${filters.location.length} locations`,
        category: 'location'
      });
    }

    if (filters.minOpenSpots > 0) {
      result.push({
        key: 'minOpenSpots',
        label: `${filters.minOpenSpots}+ spots`,
        category: 'availability'
      });
    }

    if (filters.urgencyLevel && filters.urgencyLevel !== 'None') {
      result.push({
        key: 'urgencyLevel',
        label: `Urgency: ${filters.urgencyLevel}`,
        category: 'availability'
      });
    }

    if (filters.specialties?.length > 0) {
      result.push({
        key: 'specialties',
        label: filters.specialties.length === 1
          ? filters.specialties[0]
          : `${filters.specialties.length} specialties`,
        category: 'specialties'
      });
    }

    if (filters.titles?.length > 0) {
      result.push({
        key: 'titles',
        label: filters.titles.length === 1
          ? filters.titles[0]
          : `${filters.titles.length} titles`,
        category: 'qualifications'
      });
    }

    if (filters.professionalLicenses?.length > 0) {
      result.push({
        key: 'professionalLicenses',
        label: filters.professionalLicenses.length === 1
          ? filters.professionalLicenses[0]
          : `${filters.professionalLicenses.length} licenses`,
        category: 'qualifications'
      });
    }

    if (filters.treatmentModalities?.length > 0) {
      result.push({
        key: 'treatmentModalities',
        label: filters.treatmentModalities.length === 1
          ? filters.treatmentModalities[0]
          : `${filters.treatmentModalities.length} modalities`,
        category: 'treatment'
      });
    }

    if (filters.maxIndividualRate > 0) {
      result.push({
        key: 'maxIndividualRate',
        label: `≤$${filters.maxIndividualRate}`,
        category: 'price'
      });
    }

    if (filters.maxFamilyRate > 0) {
      result.push({
        key: 'maxFamilyRate',
        label: `Family ≤$${filters.maxFamilyRate}`,
        category: 'price'
      });
    }

    if (filters.clientTypes?.length > 0) {
      result.push({
        key: 'clientTypes',
        label: filters.clientTypes.length === 1
          ? filters.clientTypes[0]
          : `${filters.clientTypes.length} client types`,
        category: 'demographics'
      });
    }


    if (filters.underCapacity) {
      result.push({
        key: 'underCapacity',
        label: 'Under capacity',
        category: 'capacity'
      });
    }

    if (filters.timezone) {
      result.push({
        key: 'timezone',
        label: filters.timezone,
        category: 'location'
      });
    }

    if (filters.sessionTypes?.length > 0) {
      result.push({
        key: 'sessionTypes',
        label: filters.sessionTypes.length === 1
          ? filters.sessionTypes[0]
          : `${filters.sessionTypes.length} session types`,
        category: 'session'
      });
    }

    if (filters.insuranceProviders?.length > 0) {
      result.push({
        key: 'insuranceProviders',
        label: filters.insuranceProviders.length === 1
          ? filters.insuranceProviders[0]
          : `${filters.insuranceProviders.length} insurance plans`,
        category: 'insurance'
      });
    }

    if (filters.languages?.length > 0) {
      result.push({
        key: 'languages',
        label: filters.languages.length === 1
          ? filters.languages[0]
          : `${filters.languages.length} languages`,
        category: 'language'
      });
    }

    return result;
  }, [filters]);

  // FIXED: Clear single filter function
  const clearSingleFilter = useCallback((filterToRemove: SelectedFilter) => {
    const clearActions = {
      verificationStatus: () => onFilterChange('verificationStatus', ''),
      location: () => {
        onFilterChange('state', '');
        onFilterChange('city', '');
        onFilterChange('zipCode', '');
      },
      officeLocation: () => onFilterChange('officeLocation', ''),
      practiceLocations: () => onFilterChange('location', []),
      minOpenSpots: () => onFilterChange('minOpenSpots', 0),
      urgencyLevel: () => onFilterChange('urgencyLevel', ''),
      specialties: () => onFilterChange('specialties', []),
      titles: () => onFilterChange('titles', []),
      professionalLicenses: () => onFilterChange('professionalLicenses', []),
      underCapacity: () => onFilterChange('underCapacity', false),
      timezone: () => onFilterChange('timezone', ''),
      treatmentModalities: () => onFilterChange('treatmentModalities', []),
      clientTypes: () => onFilterChange('clientTypes', []),
      sessionTypes: () => onFilterChange('sessionTypes', []),
      insuranceProviders: () => onFilterChange('insuranceProviders', []),
      languages: () => onFilterChange('languages', []),
      maxIndividualRate: () => onFilterChange('maxIndividualRate', 0),
      maxFamilyRate: () => onFilterChange('maxFamilyRate', 0)
    };

    clearActions[filterToRemove.key]?.();
  }, [onFilterChange]);

  // FIXED: Clear all filters function — reset location back to practice offices
  const clearAllFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS, location: practiceLocations });
  }, [practiceLocations]);

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return selectedFilters.length > 0;
  }, [selectedFilters]);

  // Multi-select section configurations
  const multiSelectSections = [
    {
      key: 'specialties',
      title: 'Specialties',
      options: FILTER_CONFIG.specialties,
      config: {
        icon: '🧠',
        gradient: 'from-rose-50 to-pink-50',
        border: 'border-rose-100',
        bg: 'bg-rose-500',
        text: 'text-rose-900'
      }
    },
    {
      key: 'treatmentModalities',
      title: 'Modalities',
      options: FILTER_CONFIG.treatmentModalities,
      config: {
        icon: '🎯',
        gradient: 'from-cyan-50 to-blue-50',
        border: 'border-cyan-100',
        bg: 'bg-cyan-500',
        text: 'text-cyan-900'
      }
    },
    {
      key: 'ages',
      title: 'Age Range',
      options: FILTER_CONFIG.ages,
      config: {
        icon: '👥',
        gradient: 'from-green-50 to-emerald-50',
        border: 'border-green-100',
        bg: 'bg-green-500',
        text: 'text-green-900'
      }
    },
    {
      key: 'sessionTypes',
      title: 'Session Types',
      options: FILTER_CONFIG.sessionTypes,
      config: {
        icon: '💬',
        gradient: 'from-violet-50 to-purple-50',
        border: 'border-violet-100',
        bg: 'bg-violet-500',
        text: 'text-violet-900'
      }
    },
    {
      key: 'insuranceProviders',
      title: 'Insurance Accepted',
      options: FILTER_CONFIG.insuranceProviders,
      config: {
        icon: '🏥',
        gradient: 'from-slate-50 to-gray-50',
        border: 'border-slate-100',
        bg: 'bg-slate-500',
        text: 'text-slate-900'
      }
    },
    {
      key: 'languages',
      title: 'Languages',
      options: FILTER_CONFIG.languages,
      config: {
        icon: '🌍',
        gradient: 'from-teal-50 to-cyan-50',
        border: 'border-teal-100',
        bg: 'bg-teal-500',
        text: 'text-teal-900'
      }
    }
  ];

  // Render Results View
  if (searchResults && showResults) {
    return (
      <ResultsView
        searchResults={searchResults}
        selectedFilters={selectedFilters}
        viewMode={resultsViewMode}
        onViewModeChange={setResultsViewMode}
        onBackToFilters={() => {
          setShowResults(false);
          setSuccessModal(false);
        }}
        onRemoveFilter={clearSingleFilter}
        onClearAllFilters={clearAllFilters}
      />
    );
  }

  // Render Filter Form
  return (
    <div className="flex-1 overflow-auto bg-gray-50/40">
      <div className="p-6 sm:p-8">

        {/* ── Dev banner (compact) ─────────────────────────────── */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
          <div className="w-6 h-6 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Brain className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <p className="text-sm text-amber-700">
            <span className="font-semibold">Development Preview</span> — early access to intelligent matchmaking. Features may change.
          </p>
        </div>

        {/* ── Hero ─────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl sm:rounded-2xl px-6 py-5 sm:py-6 mb-8 relative overflow-hidden">
          {/* subtle noise layer */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_#fff_0%,_transparent_70%)]" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">🧠 Client–Clinician Matching</h2>
              <p className="text-purple-200 text-sm">Find the optimal therapist based on client needs and real-time availability</p>
            </div>
            <div className="inline-flex items-center gap-2 self-start sm:self-auto bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2 flex-shrink-0">
              <Check className="w-4 h-4 text-green-300" />
              <span className="text-sm text-white font-medium">Staff Data Synced</span>
            </div>
          </div>
        </div>

        {/* ── Stats row ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-blue-700">Staff Clinicians</p>
              <p className="text-xl font-bold text-blue-900">{searchResults?.data?.length || 0}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-emerald-700">Avg Utilization</p>
              <p className="text-xl font-bold text-emerald-900">
                {(() => {
                  if (!searchResults?.data?.length) return '—';
                  const tot = searchResults.data.reduce((s: number, t: any) =>
                    s + ((t.caseLoad?.currentClients || 0) / (t.caseLoad?.maxCapacity || 1) * 100), 0);
                  return `${Math.round(tot / searchResults.data.length)}%`;
                })()}
              </p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-100 flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-purple-700">Available Today</p>
              <p className="text-xl font-bold text-purple-900">
                {(() => {
                  if (!searchResults?.data?.length) return '—';
                  return searchResults.data.reduce((c: number, t: any) => c + ((t.availability?.openSpots || 0) > 0 ? 1 : 0), 0);
                })()}
              </p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-orange-700">Open Spots</p>
              <p className="text-xl font-bold text-orange-900">
                {(() => {
                  if (!searchResults?.data?.length) return '—';
                  return searchResults.data.reduce((tot: number, t: any) => tot + (t.availability?.openSpots || 0), 0);
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* ── Main grid ────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">

          {/* LEFT: Filter forms */}
          <div className="lg:col-span-2 space-y-6">

            {/* Popular Filters */}
            <MetricsSection
              filters={filters}
              onFilterChange={onFilterChange}
              locationInput={locationInput}
              onLocationInputChange={setLocationInput}
              onAddLocation={addLocationFilter}
              onRemoveLocation={removeLocationFilter}
              practiceLocations={practiceLocations}
            />

            {/* Professional Qualifications */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-100">
              <FilterSectionHeader
                icon={<Award />}
                title="Professional Qualifications"
                iconBg="bg-purple-500"
                textColor="text-purple-900"
                activeCount={(filters.titles?.length || 0) + (filters.professionalLicenses?.length || 0)}
              />
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-purple-800 mb-3 pl-2 border-l-4 border-purple-400">Titles</label>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {FILTER_CONFIG.professionalTitles.map(title => (
                      <button
                        key={title} type="button"
                        onClick={() => toggleArrayFilter('titles', title)}
                        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                          filters.titles?.includes(title)
                            ? 'bg-white shadow-lg border-2 border-purple-300 scale-105 font-semibold'
                            : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105'
                        }`}
                      >{title}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-purple-800 mb-3 pl-2 border-l-4 border-violet-400">Licences</label>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {FILTER_CONFIG.professionalLicenseTypes.map(lic => (
                      <button
                        key={lic} type="button"
                        onClick={() => toggleArrayFilter('professionalLicenses', lic)}
                        className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                          filters.professionalLicenses?.includes(lic)
                            ? 'bg-white shadow-lg border-2 border-violet-300 scale-105 font-semibold'
                            : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105'
                        }`}
                      >{lic}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Specialties — pill MultiSelect with show-more */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-rose-100">
              <FilterSectionHeader
                icon="🧠"
                title="Specialties"
                iconBg="bg-rose-500"
                textColor="text-rose-900"
                activeCount={filters.specialties?.length || 0}
              />
              <div className="flex flex-wrap gap-2 sm:gap-3 max-h-60 overflow-y-auto">
                {(showAllSpecialties ? FILTER_CONFIG.specialties : FILTER_CONFIG.specialties.slice(0, 18)).map(s => (
                  <button
                    key={s} type="button"
                    onClick={() => toggleArrayFilter('specialties', s)}
                    className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                      filters.specialties?.includes(s)
                        ? 'bg-white shadow-lg border-2 border-rose-200 scale-105 font-semibold'
                        : 'bg-white/70 border border-gray-200 hover:bg-white hover:shadow-md hover:scale-105'
                    }`}
                  >{s}</button>
                ))}
              </div>
              {FILTER_CONFIG.specialties.length > 18 && (
                <button
                  type="button"
                  onClick={() => setShowAllSpecialties(!showAllSpecialties)}
                  className="mt-4 flex items-center gap-1.5 text-sm text-rose-600 hover:text-rose-700 font-medium transition-colors"
                >
                  {showAllSpecialties ? <><ChevronUp className="w-4 h-4" />Show Less</> : <><ChevronDown className="w-4 h-4" />View All {FILTER_CONFIG.specialties.length} Specialties</>}
                </button>
              )}
            </div>

            {/* Remaining multi-select sections from config (Modalities, Ages, Session Types, Insurance, Languages) */}
            {multiSelectSections
              .filter(s => s.key !== 'specialties')
              .map(section => (
                <MultiSelectSection
                  key={section.key}
                  title={section.title}
                  filterKey={section.key === 'ages' ? 'ageRange' : section.key}
                  options={section.options}
                  filters={filters}
                  onToggle={toggleArrayFilter}
                  config={section.config}
                />
              ))}

            {/* Pricing */}
            <PricingSection filters={filters} onFilterChange={onFilterChange} />

            {/* Action bar */}
            <div className="flex items-center justify-between gap-4 pt-2 pb-4">
              <button
                type="button"
                onClick={onClearFilters}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all duration-200"
              >
                <X className="w-4 h-4" />
                Clear All
              </button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-sm sm:text-base"
              >
                {isSubmitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</>
                  : <><Search className="w-4 h-4" /> Find Matching Clinicians</>}
              </motion.button>
            </div>
          </div>

          {/* RIGHT: Live results preview */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-100">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Matched Clinicians</h3>
                    <p className="text-xs text-slate-500">{searchResults?.data?.length || 0} found</p>
                  </div>
                </div>
                <div className="inline-flex items-center p-1 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm border border-slate-100">
                  <button
                    type="button"
                    onClick={() => setResultsViewMode('gallery')}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg transition-colors ${resultsViewMode === 'gallery' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    aria-label="Gallery view"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setResultsViewMode('list')}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg transition-colors ${resultsViewMode === 'list' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    aria-label="List view"
                  >
                    <List className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className={`${resultsViewMode === 'gallery' ? 'grid grid-cols-1 gap-4' : 'space-y-4'} max-h-[520px] overflow-y-auto`}>
                {(searchResults?.data?.length ?? 0) > 0 ? (
                  searchResults.data.map((therapist, index) => (
                    <div key={therapist._id || index}>
                      <TherapistCard therapist={therapist} index={index} />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 bg-white/80 backdrop-blur-sm border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <Users className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">No results yet</p>
                    <p className="text-xs text-slate-400 mt-1">Set your filters and run the search</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modals */}
      <LoadingModal isVisible={isSubmitting} message="Searching for therapists…" />
      <ErrorModal error={submitError} onClose={clearError} />
      <SuccessModal isVisible={showSuccessModal} resultCount={searchResults?.data?.length} onViewResults={handleViewResults} />
    </div>
  );
};

export default MatchmakingFilters;
